import { Controller } from "@hotwired/stimulus"

// The work register's processes (playbook/_process_row): a caret opens a
// process onto its steps, like subtasks on /tasks, and Expand all opens every
// one. Which processes are open is remembered per viewer in localStorage. When
// a filter picked the steps (a person, a search, unassigned), the server opens
// the processes itself and nothing is restored or saved over it.
const STORAGE_KEY = "playbook_open_processes"

export default class extends Controller {
    static targets = ["process", "toggleAll"]
    static values = { opened: Boolean }

    connect() {
        if (!this.openedValue) {
            let saved = this._saved()
            this.processTargets.forEach((process) => this._set(process, saved.has(process.dataset.processId)))
        }
        this._syncToggleAll()
    }

    toggle(event) {
        let process = event.currentTarget.closest("[data-playbook-processes-target='process']")
        if (!process) return

        let open = process.dataset.collapsed === "true"
        this._sameProcess(process.dataset.processId).forEach((each) => this._set(each, open))
        this._save()
        this._syncToggleAll()
    }

    toggleAll() {
        let open = this.processTargets.some((process) => process.dataset.collapsed === "true")
        this.processTargets.forEach((process) => this._set(process, open))
        this._save()
        this._syncToggleAll()
    }

    _set(process, open) {
        process.dataset.collapsed = open ? "false" : "true"
        let steps = process.querySelector(".playbook-process-steps")
        if (steps) steps.hidden = !open
        process.querySelectorAll("[aria-controls]").forEach((button) => button.setAttribute("aria-expanded", open ? "true" : "false"))
    }

    _sameProcess(id) {
        return this.processTargets.filter((process) => process.dataset.processId === id)
    }

    _syncToggleAll() {
        if (!this.hasToggleAllTarget) return

        let allOpen = this.processTargets.length > 0 && this.processTargets.every((process) => process.dataset.collapsed === "false")
        let label = allOpen ? "Collapse all" : "Expand all"
        this.toggleAllTarget.title = label
        this.toggleAllTarget.setAttribute("aria-label", label)
        this.toggleAllTarget.setAttribute("aria-pressed", allOpen ? "true" : "false")
        let icon = this.toggleAllTarget.querySelector("i")
        if (icon) icon.className = allOpen ? "bi bi-chevron-contract" : "bi bi-chevron-expand"
    }

    _saved() {
        try {
            return new Set(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]").map(String))
        } catch (_error) {
            return new Set()
        }
    }

    // A filtered view's open processes are the filter's doing, not a choice
    // to remember. Processes not on this page (a department filter) keep
    // whatever was saved for them.
    _save() {
        if (this.openedValue) return

        let open = this._saved()
        this.processTargets.forEach((process) => {
            if (process.dataset.collapsed === "false") open.add(process.dataset.processId)
            else open.delete(process.dataset.processId)
        })
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...open]))
        } catch (_error) {
            // Private window or blocked storage: the page still works, it just forgets.
        }
    }
}
